import type {
  GameState,
  GameAction,
  RoleMap,
  EggAnimVariant,
  GamePhase,
  PlayerRole,
} from "../types";
import { GAME_CONFIG } from "../config/gameConfig";

/**
 * Randomly assigns attacker and defender roles to two players.
 * @param player1Id - First player's ID
 * @param player2Id - Second player's ID
 * @returns RoleMap with each role assigned to a player ID
 */
export function assignRoles(player1Id: string, player2Id: string): RoleMap {
  const coinFlip = Math.random() > 0.5;
  return {
    attacker: coinFlip ? player1Id : player2Id,
    defender: coinFlip ? player2Id : player1Id,
  };
}

/**
 * Resolves a round, returning the winner's ID.
 * If predefinedWinner is provided and matches one of the players, that player
 * always wins. Otherwise falls back to random 50/50.
 * @param player1Id - First player's ID
 * @param player2Id - Second player's ID
 * @param predefinedWinner - Optional player ID that must win; ignored if it
 *   does not match either player
 * @returns The ID of the round winner
 */
export function resolveRound(
  player1Id: string,
  player2Id: string,
  predefinedWinner?: string | null,
): string {
  if (
    predefinedWinner &&
    (predefinedWinner === player1Id || predefinedWinner === player2Id)
  ) {
    return predefinedWinner;
  }
  return Math.random() > 0.5 ? player1Id : player2Id;
}

/**
 * After all MAX_ROUNDS have been played, returns the ID of the player with
 * more wins, or null if both players have the same number of wins (tie).
 * @param wins - Map of player ID → win count
 * @returns The winning player's ID, or null for a tie
 */
export function determineMatchWinner(
  wins: Record<string, number>,
): string | null {
  const entries = Object.entries(wins);
  const [[id1, c1], [id2, c2]] = entries;
  if (c1 === c2) return null;
  return c1 > c2 ? id1 : id2;
}

/**
 * Creates the initial game state with randomly assigned roles.
 * @param player1Id - First player's ID
 * @param player2Id - Second player's ID
 * @returns A fresh GameState ready for the first countdown
 */
export function createInitialState(
  player1Id: string,
  player2Id: string,
): GameState {
  return {
    phase: "waiting_ready",
    countdownValue: 3,
    currentRound: 1,
    roles: assignRoles(player1Id, player2Id),
    roundWinnerId: null,
    roundLoserId: null,
    wins: { [player1Id]: 0, [player2Id]: 0 },
    roundHistory: [],
    gameWinnerId: null,
  };
}

/**
 * Pure reducer for game state transitions.
 * All state changes flow through here — no side effects.
 */
export function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case "START_COUNTDOWN":
      return { ...state, phase: "countdown", countdownValue: 3 };

    case "COUNTDOWN_TICK": {
      const next = state.countdownValue - 1;
      return { ...state, countdownValue: next };
    }

    case "START_FIGHTING":
      return { ...state, phase: "fighting" };

    case "IMPACT": {
      const loserId =
        action.winnerId === state.roles.attacker
          ? state.roles.defender
          : state.roles.attacker;
      const newWins = {
        ...state.wins,
        [action.winnerId]: (state.wins[action.winnerId] ?? 0) + 1,
      };
      return {
        ...state,
        phase: "impact",
        roundWinnerId: action.winnerId,
        roundLoserId: loserId,
        wins: newWins,
        roundHistory: [...state.roundHistory, action.winnerId],
      };
    }

    case "SHOW_ROUND_RESULT":
      return { ...state, phase: "round-result" };

    case "ADVANCE": {
      if (state.roundHistory.length >= GAME_CONFIG.maxRounds) {
        const gameWinnerId = determineMatchWinner(state.wins);
        return { ...state, phase: "game-over", gameWinnerId };
      }
      return {
        ...state,
        phase: "countdown",
        countdownValue: 3,
        currentRound: state.currentRound + 1,
        roundWinnerId: null,
        roundLoserId: null,
      };
    }

    default:
      return state;
  }
}

/**
 * Derives the Framer Motion variant name for an egg based on current game phase
 * and whether this egg won or lost the most recent round.
 *
 * @param phase - Current game phase
 * @param isWinner - True if this egg won the current round
 * @param isLoser - True if this egg lost the current round
 * @returns The variant name to pass to Framer Motion
 */
export function getEggAnimVariant(
  phase: GamePhase,
  isWinner: boolean,
  isLoser: boolean,
  role: PlayerRole,
): EggAnimVariant {
  switch (phase) {
    case "idle":
    case "waiting_ready":
    case "countdown":
      return "idle";
    case "fighting":
      return role === "attacker" ? "fightingAttacker" : "fightingDefender";
    case "impact":
      // At collision moment both are at center; loser breaks immediately
      return isLoser ? "lost" : "impact";
    case "round-result":
    case "game-over":
      if (isWinner) return "won";
      if (isLoser) return "lost";
      return "idle";
    default:
      return "idle";
  }
}

export const getRandomKey = (): string => {
  const arr = ["F", "G", "H", "K", "Q", "S", "T", "L", "Y", "P"];
  return arr[Math.floor(Math.random() * arr.length)] as string;
};
